import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/Button";
import { Icon } from "../app/Icon";
import { inputClass } from "./FormKit";
import { labelize } from "../lib/format";
import { FunctionsError } from "../lib/functions-client";
import { submitFeedback, uploadFeedbackImage, FEEDBACK_TYPES, MAX_FEEDBACK_IMAGES, MAX_FEEDBACK_IMAGE_BYTES, ACCEPTED_IMAGE_TYPES } from "../lib/feedback";

export function FeedbackModal({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const [type, setType] = useState("general");
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  function addFiles(list: FileList | null) {
    if (!list) return;
    setNote(null);
    const next = [...images];
    for (const f of Array.from(list)) {
      if (next.length >= MAX_FEEDBACK_IMAGES) { setNote(`Up to ${MAX_FEEDBACK_IMAGES} screenshots.`); break; }
      if (!ACCEPTED_IMAGE_TYPES.includes(f.type)) { setNote("Only PNG, JPEG, GIF, or WebP images."); continue; }
      if (f.size > MAX_FEEDBACK_IMAGE_BYTES) { setNote("Each image must be under 10 MB."); continue; }
      next.push(f);
    }
    setImages(next);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    setNote(null);
    try {
      const { id } = await submitFeedback({ type, rating, message, context: { route: pathname } });
      // Upload screenshots (server re-validates each by magic bytes; rejected ones are skipped).
      let rejected = 0;
      for (const f of images) { if (!(await uploadFeedbackImage(id, f))) rejected += 1; }
      if (rejected > 0) setNote(`${rejected} image${rejected > 1 ? "s were" : " was"} skipped — not a supported image.`);
      setDone(true);
    } catch (err) {
      setNote(err instanceof FunctionsError ? err.message : "Could not send feedback.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Send feedback">
      <div className="relative w-full max-w-md rounded-card border border-line bg-surface p-6">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 text-muted hover:text-ink">✕</button>
        {done ? (
          <div className="grid justify-items-center gap-3 py-4 text-center">
            <span className="grid h-14 w-14 place-items-center rounded-full border-2 border-verified/40 text-verified"><Icon name="verify" className="h-7 w-7" /></span>
            <h2 className="font-display text-xl font-black tracking-tight">Thanks for the feedback!</h2>
            <p className="text-sm text-muted">We read every note — it directly shapes what we build next.</p>
            <Button onClick={onClose}>Done</Button>
          </div>
        ) : (
          <form className="grid gap-4" onSubmit={submit}>
            <div>
              <h2 className="font-display text-xl font-black tracking-tight">Send feedback</h2>
              <p className="text-sm text-muted">Help us improve RecruitTrucker.</p>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {FEEDBACK_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => setType(t)} className={`rounded-lg border px-3 py-1.5 text-sm font-bold transition-colors ${type === t ? "border-accent bg-accent/10 text-accent-strong" : "border-line text-muted hover:text-ink"}`}>
                  {labelize(t)}
                </button>
              ))}
            </div>

            <div className="grid gap-1">
              <span className="text-xs font-bold text-muted">How's your experience? (optional)</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" aria-label={`${n} star${n > 1 ? "s" : ""}`} onClick={() => setRating(n === rating ? null : n)} className={`text-2xl transition-colors ${rating && n <= rating ? "text-accent" : "text-line hover:text-muted"}`}>★</button>
                ))}
              </div>
            </div>

            <textarea className={inputClass} rows={4} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What's working, what's missing, what would help you…" aria-label="Feedback message" required />

            <div className="grid gap-2">
              <label className="flex w-max cursor-pointer items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-sm font-bold text-muted hover:text-ink">
                <Icon name="add" className="h-4 w-4" /> Add screenshots
                <input type="file" accept={ACCEPTED_IMAGE_TYPES.join(",")} multiple className="hidden" aria-label="Add screenshots"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              </label>
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="feedback-previews">
                  {images.map((f, i) => (
                    <div key={i} className="relative">
                      <img src={URL.createObjectURL(f)} alt="" className="h-14 w-14 rounded-lg border border-line object-cover" />
                      <button type="button" aria-label="Remove image" onClick={() => setImages(images.filter((_, j) => j !== i))}
                        className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-danger text-xs font-bold text-white">×</button>
                    </div>
                  ))}
                </div>
              )}
              <span className="font-mono text-[11px] text-muted">PNG, JPEG, GIF, or WebP · up to {MAX_FEEDBACK_IMAGES} · max 10 MB each</span>
            </div>

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={busy || !message.trim()}>{busy ? "Sending…" : "Send feedback"}</Button>
              {note && <span className="font-mono text-sm text-danger">{note}</span>}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
